import { useMemo } from 'react';
import { useCarousel } from './carousel';
import { cn } from '../../utils/cn';

const DOT_SIZE_REM = 0.5;
const GAP_SIZE_REM = 0.25;

/**
 * Returns the first dot index for the sliding pagination window so the active
 * slide stays visible when there are more slides than can be shown at once.
 */
export function getCarouselPaginationStartDot({
    currentSlide,
    totalSlides,
    visibleDots,
}: {
    currentSlide: number;
    totalSlides: number;
    visibleDots: number;
}): number {
    const halfVisibleDots = Math.floor(visibleDots / 2);

    if (currentSlide <= halfVisibleDots) {
        return 0;
    }

    if (currentSlide >= totalSlides - halfVisibleDots - 1) {
        return totalSlides - visibleDots;
    }

    return currentSlide - halfVisibleDots;
}

/**
 * The pagination component for the carousel.
 * @see {@link Carousel}
 */
export function CarouselPagination({
    className,
    maxDots = 5,
}: {
    className?: string;
    /**
     * The maximum number of dots to display. Defaults to `5`.
     */
    maxDots?: number;
}) {
    const { currentSlide, totalSlides } = useCarousel();
    const visibleDots = useMemo(() => {
        if (totalSlides === 0) {
            return 0;
        }

        return Math.min(maxDots, totalSlides);
    }, [maxDots, totalSlides]);

    const dotIndexes = useMemo(() => {
        if (visibleDots === 0) {
            return [];
        }

        const startDot = getCarouselPaginationStartDot({
            currentSlide,
            totalSlides,
            visibleDots,
        });

        return Array.from(
            { length: visibleDots },
            (_, offset) => startDot + offset,
        );
    }, [currentSlide, totalSlides, visibleDots]);

    if (visibleDots === 0) {
        return null;
    }

    return (
        <div
            className={cn(
                'z-10 flex shrink-0 items-center justify-center',
                className,
            )}
            data-slot="carousel-pagination"
        >
            <div
                className="flex shrink-0 items-center transition-opacity duration-500 ease-in-out"
                style={{ gap: `${GAP_SIZE_REM}rem` }}
            >
                {dotIndexes.map((dotIndex) => (
                    <div
                        key={dotIndex}
                        className={cn(
                            'shrink-0 rounded-full bg-white transition-opacity duration-500 ease-in-out',
                            currentSlide === dotIndex
                                ? 'opacity-100'
                                : 'opacity-40',
                        )}
                        style={{
                            width: `${DOT_SIZE_REM}rem`,
                            height: `${DOT_SIZE_REM}rem`,
                        }}
                    />
                ))}
            </div>
        </div>
    );
}
